import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MailOutlineOutlinedIcon from '@mui/icons-material/MailOutlineOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import Hidden from '@/components/ui/TzoHidden';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form8809Instructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Form 8809 Instructions | Information Return Extension -Taxzerone"
          description="Form 8809, extend your information return deadline by 30 days and request an additional time also. Here is the instruction to file form 8809 for your guidance."
          keywords=""
          canonical="resources/tax-extension/form-8809-instructions/"
          ogType="article"
          jsonLdPricing="11.99"
          jsonLdAggregateRatingValue="4.95"
          jsonLdAggregateRatingCount="10859"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,

            mt: 3,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Form 8809</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          It is used to extend the deadline for information Returns
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=8809instructions`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-extension/form-8809-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>
                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-08-20T09:00:00-05:00"
                      >
                        Aug 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-08-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        Aug 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>
                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form 8809 at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',
                      '& li': { mb: 1.6, position: 'relative', pl: 3 },
                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}> Form 8809</span> is
                        filed by payers and other information return filers with
                        the IRS to request an
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          automatic 30-day extension
                        </span>{' '}
                        to file eligible information returns, including Forms{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          W-2, 1099, 1098, 1095, 1094-C, 5498, W-2G, 3921, 3922,
                          8027,
                        </span>{' '}
                        and others.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <Link
                          href="/resources/tax-extension/form-8809-due-dates/"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          Due Date
                        </Link>
                        : Form 8809 must be filed{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          on or before the original due date{' '}
                        </span>{' '}
                        of the return. In certain situations, filers may qualify
                        for an{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          additional 30-day extension
                        </span>
                        .
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <span style={{ fontWeight: 600 }}> Penalties:</span> The
                        penalty ranges from{' '}
                        <span style={{ fontWeight: 600 }}>
                          {' '}
                          $60 to $680 per return
                        </span>
                        , depending on how late the return is filed and whether
                        the failure was due to intentional disregard.
                      </span>
                    </li>
                  </Box>
                </Box>
                <Typography variant="body1" sx={{ mb: 4 }}>
                  Form 8809, Application for Extension of Time to File
                  Information Returns. It is an IRS form used to request
                  additional time to file the information returns such as{' '}
                  <Link
                    href="/resources/information-returns/form-w-2-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form W-2
                  </Link>
                  ,{' '}
                  <Link
                    href="/resources/information-returns/form-1097-btc-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1097
                  </Link>
                  ,{' '}
                  <Link
                    href="/resources/information-returns/form-1098-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1098
                  </Link>
                  ,{' '}
                  <Link href="/form-1099-series/" sx={{ color: '#0202ff' }}>
                    1099
                  </Link>
                  ,{' '}
                  <Link
                    href="/resources/information-returns/form-3921-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    3921
                  </Link>
                  ,{' '}
                  <Link
                    href="/resources/information-returns/form-3922-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    3922
                  </Link>
                  ,{' '}
                  <Link
                    href="/resources/information-returns/form-w-2g-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    W-2G
                  </Link>
                  ,{' '}
                  <Link
                    href="/resources/information-returns/form-1099-nec-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1099-NEC
                  </Link>
                  ,{' '}
                  <Link
                    href="/resources/information-returns/form-1042-s-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1042-S
                  </Link>
                  ,{' '}
                  <Link
                    href="/resources/information-returns/form-5498-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    5498
                  </Link>
                  ,{' '}
                  <Link
                    href="/resources/information-returns/form-5498-esa-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    5498-ESA
                  </Link>
                  ,{' '}
                  <Link
                    href="/resources/information-returns/form-5498-qa-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    5498-QA
                  </Link>
                  ,{' '}
                  <Link
                    href="/resources/information-returns/form-5498-sa-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    5498-SA
                  </Link>
                  , 8027, 1094-C, <br />
                  <Link
                    href="/resources/information-returns/form-1095-c-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1095-C
                  </Link>
                  ,{' '}
                  <Link
                    href="/resources/information-returns/form-1095-b-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    1095-B
                  </Link>
                  , 1099-QA.
                </Typography>
                <Box sx={{ mb: 1, textAlign: { xs: 'center', md: 'left' } }} />
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who can file Form 8809?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Payers/filers who could not submit their information return
                  with the IRS by the due date can{' '}
                  <Link
                    href="/tax-extension/efile-irs-form-8809-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    file form 8809
                  </Link>{' '}
                  to get an automatic 30-day extension.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 8809 Due Date
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  File Form 8809, once you know that an extension of time is
                  required to file the information return, but not before
                  January 1st of the year. It should be filed before the due
                  date of the respective information return you are filing.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The due dates for filing the extension form 8809 for the
                  below-mentioned form.{' '}
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Form
                          </TableCell>

                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Paper Filing
                          </TableCell>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            Electronic Filing
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            W-2*
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            January 31
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            -
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            W-2G
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            February 28
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            March 31{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            1042-S
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            March 15
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            March 15{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            1094-C
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            February 28
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            March 31{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            1095
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            February 28
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            March 31{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            1097, 1098, 1099
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            February 28
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            March 31{' '}
                          </TableCell>
                        </TableRow>

                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            1099-QA*
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            February 28
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            -{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            1099-NEC*
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            January 31
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            -{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            3921, 3922
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            February 28
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            March 31{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            5498
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            May 31
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            May 31{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            5498-QA*
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            May 31
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderBottom: '1px solid #828282',
                            }}
                          >
                            -{' '}
                          </TableCell>
                        </TableRow>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            8027
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Last day of February
                          </TableCell>
                          <TableCell
                            sx={{
                              textAlign: 'center',
                            }}
                          >
                            March 31{' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  * Request for extension of Forms 1099-NEC, 1099-QA, 5498-QA,
                  and W-2 must be filed on paper.
                </Typography>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Filing extension for different information return forms
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  When filing extensions for multiple information returns with
                  different deadlines, prioritize the earliest deadline. For
                  example, if you need to file extensions for both Form 1095-C
                  and Form 5498, you must file by March 31, 2026, even though
                  the deadline for Form 5498 is May 31, 2026.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 5, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> Submit the extension forms 8809 for Form 1095-C
                  and Form 5498 separately.
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Additional Extension
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  An automatic 30-day deadline extension available for Forms W-2
                  or 1099-NEC under certain conditions. Other than these forms,
                  if the filer needs more time, they can apply for a{' '}
                  <Hidden smDown>
                    {' '}
                    <br />
                  </Hidden>
                  second 30-day extension only if they meet the criteria
                  mentioned below and file before the expiry of the first 30-day
                  expiration.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 24, md: 22, sm: 20, xs: 18 },
                    fontWeight: 500,
                    mb: 1.5,
                  }}
                >
                  Criteria to apply for additional extension:{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If the business faces any severe situations such as
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li>
                    You were unable to resume operations or business documents
                    due to a catastrophic event that occurred in a federal
                    disaster region.
                  </li>
                  <li>
                    Your activities were affected by a natural disaster, fire,
                    or casualty.{' '}
                  </li>
                  <li>
                    This was your company&apos;s first year of operation.{' '}
                  </li>
                  <li>
                    The person responsible for filing is either sick, absent, or
                    passed away.{' '}
                  </li>
                  <li>
                    The filer did not receive data on a payee statement to file
                    a return on time.{' '}
                  </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 5, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> Form W-2 and 1099-NEC need to satisfy any of the
                  condition mentioned above to request Extension
                </Alert>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 1,
                  }}
                >
                  Filing methods supported by Form 8809
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 24, md: 22, sm: 20, xs: 18 },
                    fontWeight: 500,
                    mb: 1.5,
                  }}
                >
                  E-Filing:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS recommends filing form 8809, Application for Extension
                  of Time to File Information Returns using the electronic
                  filing method.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 24, md: 22, sm: 20, xs: 18 },
                    fontWeight: 500,
                    mb: 1.5,
                  }}
                >
                  Paper filing:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The Payer or Filer can file form 8809 using the paper filing
                  method by completing the form and mailing it to the IRS using
                  below address.{' '}
                </Typography>
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.8 }}>
                    {' '}
                    <MailOutlineOutlinedIcon
                      sx={{
                        position: 'relative',
                        ml: 0,
                        mr: 0.5,
                        mb: -1,
                        fontSize: 28,
                      }}
                    />{' '}
                    Mailing Address:
                  </Typography>
                  <Box
                    component="div"
                    sx={{ textAlign: 'left', fontSize: '15px important' }}
                  >
                    <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Department of the Treasury
                      </span>
                      <br />
                      Internal Revenue Service Center
                      <br />
                      Ogden, UT 84201-0209
                    </Typography>
                  </Box>
                </Box>

                <Box
                  component="div"
                  sx={{ mt: 3, mb: 5, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 500, mb: 0, fontSize: 15 }}
                      >
                        TaxZerone suggests E-filing to extend information return
                        deadline easily!
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=8809instructions`}
                      >
                        E-File Now
                      </Button>
                    </Box>
                  </Stack>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Penalty
                </Typography>
                <Typography variant="body1" sx={{ mb: 0 }}>
                  The IRS will impose the Penalty if it applies the following:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li> Fail to file before the deadline</li>
                  <li>
                    Fail to include necessary information or incorrect
                    information on a return.{' '}
                  </li>
                  <li> File on paper when you were required to e-file</li>
                  <li>Report an incorrect TIN or fail to report a TIN </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The penalty amounts are determined by how late the filings
                  are:
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.5 } }}>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Up to 30 days late:
                    </span>{' '}
                    $60 per return to an upper limit of $683,000 per year
                    ($239,000 for small businesses){' '}
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      31 days late through August 1:
                    </span>{' '}
                    $130 per return to the maximum limit of $2,049,000 per year
                    ($683,000 for small businesses)
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      After August 1 or not filed:
                    </span>{' '}
                    $340 per return to a maximum penalty of $4,098,500 per year
                    ($1,366,000 for small businesses){' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      Intentional disregard:
                    </span>{' '}
                    $680 per return with no maximum penalty{' '}
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Form 8809 Instructions - How to fill it out?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Let&apos;s see line-by-line instructions on how to fill out
                  Form 8809.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 1{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/8809-payer-img.webp" />
                  <source srcSet="/assets/images/8809-payer-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/8809-payer-img.png"
                    loading="lazy"
                    alt="Form 8809 – Line 1: Payer/filer name, complete address and email address"
                    width={676}
                    height={222}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Payer’s/filer’s name
                      </span>{' '}
                      - Enter the legal name of Payer or Filer.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}>
                        {' '}
                        Address, City, State, ZIP Code
                      </span>{' '}
                      - Enter the complete address, city, state, and Zipcode in
                      the appropriate field.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}> Contact name</span> -
                      Enter the contact&apos;s name of the person whom the IRS
                      can contact if any additional information is required.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}> Telephone number</span>{' '}
                      - Enter the phone number so that the IRS can contact you.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      <span style={{ fontWeight: 600 }}> Email address</span> -
                      Enter the email address of the filer.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 2{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/8809-taxpayer-img.webp" />
                  <source srcSet="/assets/images/8809-taxpayer-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/8809-taxpayer-img.png"
                    loading="lazy"
                    alt="Form 8809 – Line 2: Taxpayer Identification Number "
                    width={230}
                    height={82}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Enter the 9-digit Taxpayer identification number (TIN) or
                  Employer identification number (EIN)or qualified intermediary
                  employer identification number (QI-EIN)or withholding foreign
                  partnership employer identification number (WP-EIN) or
                  withholding foreign trust employer identification number
                  (WT-EIN).
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 5, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b> If you don’t have an EIN or QI-EIN, enter your
                  social security number (SSN).
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 3{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/8809-check-method-img.webp" />
                  <source srcSet="/assets/images/8809-check-method-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/8809-check-method-img.png"
                    loading="lazy"
                    alt="Form 8809 – Line 3: Choose your method of filing "
                    width={313}
                    height={82}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1">
                  Select the appropriate filing method for your information
                  return:
                </Typography>
                <List sx={{ mb: 3 }}>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Check the Electronic box if you plan to file your return
                      via e-filing.
                    </ListItemText>
                  </ListItem>
                  <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}>
                      <DoubleArrowIcon />
                    </ListItemIcon>
                    <ListItemText>
                      Check the Paper box if you plan to file your return using
                      the paper filing method.
                    </ListItemText>
                  </ListItem>
                </List>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 4{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/8809-extension-img.webp" />
                  <source srcSet="/assets/images/8809-extension-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/8809-extension-img.png"
                    loading="lazy"
                    alt="Form 8809 – Line 4: enter the total number of payers/filers "
                    width={362}
                    height={82}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  If you are requesting an extension for many filers, enter the
                  total count and attach a list of names and TIN.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 5{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/8809-additional-img.webp" />
                  <source srcSet="/assets/images/8809-additional-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/8809-additional-img.png"
                    loading="lazy"
                    alt="Form 8809 – Line 5: If you already requested the automatic extension, check the box "
                    width={753}
                    height={18}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  Check the box on Line 5, if you already requested for the
                  automatic extension and now you are requesting a second
                  extension.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 6{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/8809-number-return-img.webp" />
                  <source srcSet="/assets/images/8809-number-return-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/8809-number-return-img.png"
                    loading="lazy"
                    alt="Form 8809 – Line 6: check the box(es) that apply"
                    width={760}
                    height={143}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  Check the appropriate box(es) for the Form You Need an
                  Extension. The forms include
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.5 } }}>
                  <li> W-2 </li>
                  <li>1097, 1098, 1099, 3921, 3922, W-2G </li>
                  <li>1099-NEC </li>
                  <li>1042-S </li>
                  <li>5498 </li>
                  <li>5498-ESA </li>
                  <li>5498-QA </li>
                  <li>5498-SA </li>
                  <li>8027 </li>
                  <li>1094-C, 1095-C </li>
                  <li>1095-B </li>
                  <li>1099-QA </li>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 5, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b> Note:</b>This form extends only the IRS filing deadline.
                  To request additional time for furnishing recipient copies,
                  you must{' '}
                  <Link
                    href="/tax-extension/file-irs-form-15397-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    file form 15397
                  </Link>{' '}
                  separately.
                </Alert>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  Line 7{' '}
                </Typography>
                <picture>
                  <source srcSet="/assets/images/8809-requesting-img.webp" />
                  <source srcSet="/assets/images/8809-requesting-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/8809-requesting-img.png"
                    loading="lazy"
                    alt="Form 8809 – Line 7: Check the applicable box(es) that describes your need for an extension."
                    width={760}
                    height={160}
                    className="img-responsive"
                    sx={{
                      border: '2px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography
                  variant="body1"
                  sx={{
                    fontWeight: 400,
                    mb: 2,
                    lineHeight: 1.5,
                  }}
                >
                  If a filer is applying for an extension of Forms W-2 or
                  1099-NEC, or if you selected the box on Line 5, check the
                  appropriate box based on your extension needs.
                </Typography>
                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information provided is based on guidelines from the
                        Internal Revenue Service (IRS). <br />
                        For more details, please refer the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/f8809.pdf"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          IRS
                        </Link>{' '}
                        instruction for Form 8809 and{' '}
                        <Link
                          href="https://www.irs.gov/instructions/i1099gi"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          General Instructions of Information Returns
                        </Link>
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              E-file Form 8809 with TaxZerone
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              File your Form 8809 easily with TaxZerone at just{' '}
              <b>$4.99 /return</b>. <br />
              You will only have to enter the required information and transmit
              the return to the IRS.
            </Typography>

            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 22, md: 20, sm: 18, xs: 16 },
                fontWeight: 400,
                color: '#333333',
              }}
            >
              Ready to file Form 8809 online?
            </Typography>
            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=8809instructions`}
              >
                E-file Form 8809 Now
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Form8809Instructions;
